import { useCallback, useRef } from "react";
import { useState } from "react";

/**
 * Custom hook for debouncing function calls
 * @param {Function} callback - The function to debounce
 * @param {number} delay - The delay in milliseconds (default: 300ms)
 * @returns {Function} - The debounced function
 */
export const useDebounce = (callback, delay = 300) => {
  const timeoutRef = useRef(null);

  const debouncedCallback = useCallback(
    (...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay]
  );

  return debouncedCallback;
};

/**
 * Custom hook for debouncing search input
 * @param {Function} searchFunction - The search function to call
 * @param {number} delay - The delay in milliseconds (default: 300ms)
 * @returns {Object} - Object containing debounced search function and loading state
 */
export const useDebouncedSearch = (searchFunction, delay = 300) => {
  const [isSearching, setIsSearching] = useState(false);
  const timeoutRef = useRef(null);

  const debouncedSearch = useCallback(
    (searchTerm) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      if (searchTerm.length < 2) {
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      timeoutRef.current = setTimeout(async () => {
        try {
          await searchFunction(searchTerm);
        } finally {
          setIsSearching(false);
        }
      }, delay);
    },
    [searchFunction, delay]
  );

  return { debouncedSearch, isSearching };
};
